import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowDownTrayIcon,
  ArrowLeftOnRectangleIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PhoneArrowUpRightIcon,
  TrashIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  AdminRole,
  AdminUser,
  CallActivity,
  JpoRsvpStats,
  JpoRsvpSubmission,
  PostbacSubmission,
  PreinscriptionSubmission,
} from './types';
import {
  deleteJpoRsvp,
  deletePostbacLead,
  deletePreinscription,
  getCurrentAdmin,
  getErrorMessage,
  getJpoRsvps,
  getPostbacLeads,
  getPreinscriptions,
  getVisiteDetail,
  loginAdmin,
  logoutAdmin,
  patchJpoRsvpStatus,
  updateVisiteStatus,
} from './adminApi';
import {
  ADMIN_STATUS_OPTIONS,
  StatusBadge,
  defaultAdminPath,
  formatDate,
  formatRdvDate,
  formatShortDateTime,
  preinscriptionStatusBadgeClass,
  statusLabel,
} from './crmShared';
import { CallActivityList } from './CallActivityList';
import CallQueuePage from './CallQueuePage';
import PerformancePage from './PerformancePage';
import RdvCalendarPage from './RdvCalendarPage';
import UsersPage from './UsersPage';

type SubmissionKind = 'preinscriptions' | 'jpo' | 'postbac';

type SubmissionItem = PreinscriptionSubmission | JpoRsvpSubmission | PostbacSubmission;

// Shared-field accessors — postbac has `fullName`/no `niveau`; the others have `parentName`/`niveau`.
const getName = (item: SubmissionItem): string =>
  'fullName' in item ? item.fullName : item.parentName;
const getNiveau = (item: SubmissionItem): string | undefined =>
  'niveau' in item ? item.niveau : undefined;

const normalize = (value: unknown) => String(value ?? '').toLowerCase();

const exportCsvUrl = (
  kind: SubmissionKind,
  niveau: string,
  status: string,
  query: string,
  origin: '' | 'website' | 'meta' = ''
) => {
  const params = new URLSearchParams();
  if (niveau && kind !== 'postbac') params.set('niveau', niveau);
  if (status) params.set('status', status);
  if (query) params.set('search', query);
  if (kind === 'jpo' || kind === 'postbac') {
    if (origin === 'meta') params.set('source', 'meta_native');
    else if (origin === 'website') params.set('source', 'website');
  }

  const endpoint =
    kind === 'jpo'
      ? '/api/admin/jpo-rsvps/export.csv'
      : kind === 'postbac'
        ? '/api/admin/postbac/export.csv'
        : '/api/admin/visites/export.csv';
  const qs = params.toString();

  return `${endpoint}${qs ? '?' + qs : ''}`;
};

function AdminApp() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    let mounted = true;

    getCurrentAdmin()
      .then((currentUser) => {
        if (mounted) setUser(currentUser);
      })
      .catch(() => {
        if (mounted) setUser(null);
      })
      .finally(() => {
        if (mounted) setIsCheckingAuth(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Routes>
      <Route
        path="login"
        element={
          user ? (
            <Navigate to={defaultAdminPath(user)} replace />
          ) : (
            <AdminLoginPage onLogin={setUser} />
          )
        }
      />
      <Route
        element={
          <ProtectedAdmin
            isCheckingAuth={isCheckingAuth}
            user={user}
            onLogout={() => setUser(null)}
          />
        }
      >
        <Route
          index
          element={<Navigate to={user ? defaultAdminPath(user) : '/admin/login'} replace />}
        />
        <Route path="appels" element={<CallQueuePage user={user} />} />
        <Route path="agenda" element={<RdvCalendarPage />} />
        <Route
          path="visites"
          element={
            <RequireRole user={user} roles={['ADMIN']}>
              <SubmissionsPage kind="preinscriptions" title="Demandes de visite" />
            </RequireRole>
          }
        />
        <Route
          path="performance"
          element={
            <RequireRole user={user} roles={['ADMIN']}>
              <PerformancePage />
            </RequireRole>
          }
        />
        <Route
          path="utilisateurs"
          element={
            <RequireRole user={user} roles={['ADMIN']}>
              <UsersPage currentUser={user} />
            </RequireRole>
          }
        />
        <Route path="inscriptions" element={<Navigate to="/admin/visites" replace />} />
        <Route path="preinscriptions" element={<Navigate to="/admin/visites" replace />} />
        <Route
          path="portes-ouvertes"
          element={
            <RequireRole user={user} roles={['ADMIN']}>
              <SubmissionsPage kind="jpo" title="Portes ouvertes" />
            </RequireRole>
          }
        />
        <Route
          path="postbac"
          element={
            <RequireRole user={user} roles={['ADMIN']}>
              <SubmissionsPage kind="postbac" title="Prépa Post-Bac" />
            </RequireRole>
          }
        />
      </Route>
    </Routes>
  );
}

// Route-level role gate for admin-only pages. A logged-in non-admin (téléconseiller)
// is bounced to their own default landing page rather than seeing a 401 flash.
function RequireRole({
  user,
  roles,
  children,
}: {
  user: AdminUser | null;
  roles: AdminRole[];
  children: ReactNode;
}) {
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={defaultAdminPath(user)} replace />;
  return <>{children}</>;
}

function AdminLoginPage({ onLogin }: { onLogin: (user: AdminUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const user = await loginAdmin(email, password);
      onLogin(user);
      navigate(defaultAdminPath(user), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-sm p-6 md:p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
            <LockClosedIcon className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-950">Admin GSS</h1>
            <p className="text-sm text-gray-500">Accès sécurisé</p>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-gray-950 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-gray-950 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-md bg-primary px-4 py-3 font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </main>
  );
}

function ProtectedAdmin({
  isCheckingAuth,
  user,
  onLogout,
}: {
  isCheckingAuth: boolean;
  user: AdminUser | null;
  onLogout: () => void;
}) {
  const location = useLocation();

  if (isCheckingAuth) {
    return (
      <main className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-700">
        Chargement...
      </main>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  return <AdminShell user={user} onLogout={onLogout} />;
}

type NavItem = { to: string; label: string; icon: typeof PhoneArrowUpRightIcon; roles: AdminRole[] };

const NAV_ITEMS: NavItem[] = [
  { to: '/admin/appels', label: 'Suivi des appels', icon: PhoneArrowUpRightIcon, roles: ['ADMIN', 'TELECONSEILLER'] },
  { to: '/admin/agenda', label: 'Calendrier RDV', icon: CalendarIcon, roles: ['ADMIN', 'TELECONSEILLER'] },
  { to: '/admin/visites', label: 'Demandes de visite', icon: ClipboardDocumentListIcon, roles: ['ADMIN'] },
  { to: '/admin/performance', label: 'Performance', icon: ChartBarIcon, roles: ['ADMIN'] },
  { to: '/admin/utilisateurs', label: 'Utilisateurs', icon: UsersIcon, roles: ['ADMIN'] },
  { to: '/admin/portes-ouvertes', label: 'Portes ouvertes', icon: CalendarDaysIcon, roles: ['ADMIN'] },
  { to: '/admin/postbac', label: 'Prépa Post-Bac', icon: AcademicCapIcon, roles: ['ADMIN'] },
];

function AdminShell({ user, onLogout }: { user: AdminUser; onLogout: () => void }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logoutAdmin().catch(() => undefined);
    onLogout();
    navigate('/admin/login', { replace: true });
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive
        ? 'bg-primary text-white'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-950'
    }`;

  const items = NAV_ITEMS.filter((item) => item.roles.includes(user.role));

  return (
    <main className="min-h-screen bg-gray-100 text-gray-950">
      <div className="flex min-h-screen">
        <aside className="hidden w-72 shrink-0 border-r border-gray-200 bg-white px-4 py-5 lg:block">
          <div className="mb-8 px-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">GSS</p>
            <h1 className="mt-1 text-xl font-bold">Administration</h1>
          </div>
          <nav className="space-y-1">
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={navLinkClass}>
                <Icon className="h-5 w-5" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <section className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
            <div className="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between lg:px-8">
              <nav className="-mx-1 flex gap-2 overflow-x-auto px-1 lg:hidden">
                {items.map(({ to, label, icon: Icon }) => (
                  <NavLink key={to} to={to} className={navLinkClass}>
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    {label}
                  </NavLink>
                ))}
              </nav>
              <div className="flex items-center justify-between gap-4 md:ml-auto">
                <div className="flex min-w-0 items-center gap-2 text-sm text-gray-600">
                  <UserCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{user.name ?? user.email}</span>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <ArrowLeftOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                  Déconnexion
                </button>
              </div>
            </div>
          </header>
          <Outlet />
        </section>
      </div>
    </main>
  );
}

const STATUS_OPTIONS = ['nouveau', 'confirme', 'venu', 'absent'] as const;

function getOrigin(item: SubmissionItem): 'meta' | 'website' {
  return item.source === 'meta_native' ? 'meta' : 'website';
}

function SubmissionsPage({ kind, title }: { kind: SubmissionKind; title: string }) {
  const [items, setItems] = useState<SubmissionItem[]>([]);
  const [jpoStats, setJpoStats] = useState<JpoRsvpStats | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [query, setQuery] = useState('');
  const [niveau, setNiveau] = useState('');
  const [status, setStatus] = useState('');
  const [origin, setOrigin] = useState<'' | 'website' | 'meta'>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    setError('');

    if (kind === 'preinscriptions') {
      getPreinscriptions()
        .then((data) => {
          if (!mounted) return;
          setItems(data);
          setSelectedId(data[0]?.id ?? '');
        })
        .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
        .finally(() => { if (mounted) setIsLoading(false); });
    } else if (kind === 'jpo') {
      getJpoRsvps()
        .then(({ stats, data }) => {
          if (!mounted) return;
          setJpoStats(stats);
          setItems(data);
          setSelectedId(data[0]?.id ?? '');
        })
        .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
        .finally(() => { if (mounted) setIsLoading(false); });
    } else {
      getPostbacLeads()
        .then((data) => {
          if (!mounted) return;
          setItems(data);
          setSelectedId(data[0]?.id ?? '');
        })
        .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
        .finally(() => { if (mounted) setIsLoading(false); });
    }

    return () => { mounted = false; };
  }, [kind]);

  const handleStatusChange = async (itemId: string, newStatus: string) => {
    setItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, status: newStatus } : it))
    );
    try {
      await patchJpoRsvpStatus(itemId, newStatus);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleVisiteUpdated = (updated: PreinscriptionSubmission) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
  };

  const niveaux = useMemo(
    () => Array.from(new Set(items.map(getNiveau).filter(Boolean) as string[])).sort(),
    [items]
  );
  const statuses = useMemo(
    () => Array.from(new Set(items.map((item) => item.status).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const search = normalize(query);
    return items.filter((item) => {
      // postbac has no niveau and hides the niveau select — never filter it by a
      // niveau value left over from the visites/JPO tabs (state persists across tabs).
      const matchesNiveau = kind === 'postbac' || !niveau || getNiveau(item) === niveau;
      const matchesStatus = !status || item.status === status;
      const matchesOrigin = !origin || getOrigin(item) === origin;
      let matchesQuery = !search;
      if (search) {
        if (kind === 'postbac') {
          const pb = item as PostbacSubmission;
          matchesQuery =
            normalize(pb.fullName).includes(search) ||
            normalize(pb.phone).includes(search) ||
            normalize(pb.email).includes(search) ||
            normalize(pb.pole).includes(search) ||
            normalize(pb.quiRemplit).includes(search) ||
            normalize(pb.message).includes(search);
        } else {
          matchesQuery =
            normalize(getName(item)).includes(search) ||
            normalize(item.phone).includes(search) ||
            normalize(item.email).includes(search) ||
            normalize(getNiveau(item)).includes(search) ||
            (kind === 'preinscriptions'
              ? normalize((item as PreinscriptionSubmission).quartier).includes(search)
              : normalize((item as JpoRsvpSubmission).prenomEnfant).includes(search));
        }
      }

      return matchesNiveau && matchesStatus && matchesOrigin && matchesQuery;
    });
  }, [items, kind, niveau, origin, query, status]);

  const selected = filtered.find((item) => item.id === selectedId) ?? filtered[0];

  useEffect(() => {
    if (filtered.length && !filtered.some((item) => item.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const handleDelete = async (item: SubmissionItem) => {
    const label =
      kind === 'preinscriptions'
        ? `la demande de visite de ${getName(item)}`
        : kind === 'jpo'
          ? `le RSVP portes ouvertes de ${getName(item)}`
          : `le lead prépa post-bac de ${getName(item)}`;

    if (!window.confirm(`Supprimer définitivement ${label} ?`)) {
      return;
    }

    setError('');
    setIsDeleting(true);

    try {
      if (kind === 'preinscriptions') {
        await deletePreinscription(item.id);
      } else if (kind === 'jpo') {
        await deleteJpoRsvp(item.id);
      } else {
        await deletePostbacLead(item.id);
      }

      setItems((prev) => {
        const next = prev.filter((entry) => entry.id !== item.id);
        setSelectedId(next[0]?.id ?? '');
        return next;
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="px-4 py-6 lg:px-8">
      {kind === 'jpo' && jpoStats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total RSVP', value: jpoStats.total },
            { label: 'Maternelle', value: jpoStats.maternelle },
            { label: 'Primaire', value: jpoStats.primaire },
            { label: 'Dernières 24h', value: jpoStats.last24h },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm text-center">
              <p className="text-2xl font-bold text-gray-950">{value}</p>
              <p className="text-sm text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-950">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{filtered.length} soumission(s)</p>
        </div>
        <div className="grid gap-3 md:grid-cols-[minmax(220px,320px)_180px_160px_160px_160px_auto]">
          <label className="relative block">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Recherche"
              className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>
          {kind !== 'postbac' && (
            <select
              value={niveau}
              onChange={(event) => setNiveau(event.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              aria-label="Filtrer par niveau"
            >
              <option value="">Tous les niveaux</option>
              {niveaux.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          )}
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Filtrer par statut"
          >
            <option value="">Tous les statuts</option>
            {statuses.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            value={origin}
            onChange={(event) => setOrigin(event.target.value as '' | 'website' | 'meta')}
            className="rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Filtrer par origine"
          >
            <option value="">Toutes les origines</option>
            <option value="website">Site web</option>
            <option value="meta">Meta Lead</option>
          </select>
          <a
            href={exportCsvUrl(kind, niveau, status, query, origin)}
            download
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            Export CSV
          </a>
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">{kind === 'postbac' ? 'Nom' : 'Parent'}</th>
                  {kind === 'jpo' && <th className="px-4 py-3">Enfant</th>}
                  {kind !== 'postbac' && <th className="px-4 py-3">Niveau</th>}
                  {kind === 'postbac' && <th className="px-4 py-3">Pôle</th>}
                  {kind === 'postbac' && <th className="px-4 py-3">Qui remplit</th>}
                  <th className="px-4 py-3">Origine</th>
                  <th className="px-4 py-3">Téléphone</th>
                  <th className="px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={kind === 'preinscriptions' ? 6 : 7} className="px-4 py-10 text-center text-gray-500">
                      Chargement...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={kind === 'preinscriptions' ? 6 : 7} className="px-4 py-10 text-center text-gray-500">
                      Aucune soumission
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => (
                    <tr
                      key={item.id}
                      className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                        selected?.id === item.id ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => setSelectedId(item.id)}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(item.createdAt)}</td>
                      <td className="px-4 py-3 font-medium text-gray-950">{getName(item)}</td>
                      {kind === 'jpo' && (
                        <td className="px-4 py-3 text-gray-700">{(item as JpoRsvpSubmission).prenomEnfant}</td>
                      )}
                      {kind !== 'postbac' && (
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">{getNiveau(item)}</td>
                      )}
                      {kind === 'postbac' && (
                        <td className="px-4 py-3 text-gray-700">{(item as PostbacSubmission).pole}</td>
                      )}
                      {kind === 'postbac' && (
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">{(item as PostbacSubmission).quiRemplit}</td>
                      )}
                      <td className="whitespace-nowrap px-4 py-3">
                        {getOrigin(item) === 'meta' ? (
                          <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                            Meta Lead
                          </span>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                            Site web
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{item.phone}</td>
                      <td className="whitespace-nowrap px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {kind === 'jpo' ? (
                          <select
                            value={item.status}
                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                            className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
                            aria-label="Statut"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        ) : (
                          <StatusBadge status={item.status} />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {kind === 'preinscriptions' ? (
          <VisiteDetailPanel
            item={selected as PreinscriptionSubmission | undefined}
            isDeleting={isDeleting}
            onDelete={handleDelete}
            onUpdated={handleVisiteUpdated}
          />
        ) : (
          <DetailPanel
            item={selected}
            kind={kind}
            isDeleting={isDeleting}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
}

// Enriched detail panel for visite leads: contact info, an editable funnel status
// (with RDV date/heure when relevant), and the full call history.
function VisiteDetailPanel({
  item,
  isDeleting,
  onDelete,
  onUpdated,
}: {
  item?: PreinscriptionSubmission;
  isDeleting: boolean;
  onDelete: (item: PreinscriptionSubmission) => void;
  onUpdated: (updated: PreinscriptionSubmission) => void;
}) {
  const [detail, setDetail] = useState<{ callActivities: CallActivity[] } | null>(null);
  const [targetStatus, setTargetStatus] = useState('');
  const [rdvDateInput, setRdvDateInput] = useState('');
  const [rdvHeureInput, setRdvHeureInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusNotice, setStatusNotice] = useState('');

  const itemId = item?.id;

  useEffect(() => {
    setStatusError('');
    setStatusNotice('');
    if (!item) {
      setDetail(null);
      return;
    }
    setTargetStatus(item.status);
    setRdvDateInput(item.rdvDate ? item.rdvDate.slice(0, 10) : '');
    setRdvHeureInput(item.rdvHeure ?? '');

    let mounted = true;
    getVisiteDetail(item.id)
      .then((d) => { if (mounted) setDetail({ callActivities: d.callActivities }); })
      .catch(() => { if (mounted) setDetail(null); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  if (!item) {
    return (
      <aside className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm text-sm text-gray-500">
        Aucun détail
      </aside>
    );
  }

  const dirty =
    targetStatus !== item.status ||
    (targetStatus === 'rdv_pris' &&
      (rdvDateInput !== (item.rdvDate ? item.rdvDate.slice(0, 10) : '') || rdvHeureInput !== (item.rdvHeure ?? '')));

  const saveStatus = async () => {
    setSaving(true);
    setStatusError('');
    setStatusNotice('');
    try {
      const payload: { status: string; rdvDate?: string; rdvHeure?: string } = { status: targetStatus };
      if (targetStatus === 'rdv_pris') {
        payload.rdvDate = rdvDateInput;
        payload.rdvHeure = rdvHeureInput;
      }
      const updated = await updateVisiteStatus(item.id, payload);
      onUpdated(updated);
      setStatusNotice('Statut mis à jour.');
      const d = await getVisiteDetail(item.id).catch(() => null);
      if (d) setDetail({ callActivities: d.callActivities });
    } catch (err) {
      setStatusError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="break-words text-lg font-bold text-gray-950">{item.parentName}</h3>
          <p className="text-sm text-gray-500">{formatDate(item.createdAt)}</p>
        </div>
        <button
          type="button"
          onClick={() => onDelete(item)}
          disabled={isDeleting}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Supprimer la soumission"
        >
          <TrashIcon className="h-4 w-4" aria-hidden="true" />
          {isDeleting ? 'Suppression...' : 'Supprimer'}
        </button>
      </div>

      {/* Status editor */}
      <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Statut</span>
          <StatusBadge status={item.status} />
        </div>
        <select
          value={targetStatus}
          onChange={(e) => setTargetStatus(e.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {/* `a_rappeler` is set via the call queue (needs a callback date the admin
              status contract can't carry) — only show it if the lead is already there. */}
          {ADMIN_STATUS_OPTIONS.filter((s) => s !== 'a_rappeler' || item.status === 'a_rappeler').map((s) => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
        {targetStatus === 'rdv_pris' && (
          <div className="mt-2 flex gap-2">
            <input
              type="date"
              value={rdvDateInput}
              onChange={(e) => setRdvDateInput(e.target.value)}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="time"
              value={rdvHeureInput}
              onChange={(e) => setRdvHeureInput(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        {statusError && <p className="mt-2 text-sm text-red-700">{statusError}</p>}
        {statusNotice && <p className="mt-2 text-sm text-emerald-700">{statusNotice}</p>}
        <button
          type="button"
          onClick={saveStatus}
          disabled={saving || !dirty}
          className="mt-3 w-full rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer le statut'}
        </button>
      </div>

      <dl className="space-y-4">
        {preinscriptionFields(item)
          .filter(({ label }) => label !== 'Statut')
          .map(({ label, value }) => (
            <div key={label}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
              <dd className="mt-1 break-words text-sm text-gray-900">{value || '-'}</dd>
            </div>
          ))}
      </dl>

      <div className="mt-6 border-t border-gray-100 pt-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Historique des appels</p>
        {detail ? <CallActivityList activities={detail.callActivities} /> : <p className="text-sm text-gray-400">Chargement…</p>}
      </div>
    </aside>
  );
}

function DetailPanel({
  item,
  kind,
  isDeleting,
  onDelete,
}: {
  item?: SubmissionItem;
  kind: SubmissionKind;
  isDeleting: boolean;
  onDelete: (item: SubmissionItem) => void;
}) {
  if (!item) {
    return (
      <aside className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm text-sm text-gray-500">
        Aucun détail
      </aside>
    );
  }

  const fields =
    kind === 'preinscriptions'
      ? preinscriptionFields(item as PreinscriptionSubmission)
      : kind === 'jpo'
        ? jpoFields(item as JpoRsvpSubmission)
        : postbacFields(item as PostbacSubmission);

  return (
    <aside className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="break-words text-lg font-bold text-gray-950">{getName(item)}</h3>
          <p className="text-sm text-gray-500">{formatDate(item.createdAt)}</p>
        </div>
        <button
          type="button"
          onClick={() => onDelete(item)}
          disabled={isDeleting}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Supprimer la soumission"
        >
          <TrashIcon className="h-4 w-4" aria-hidden="true" />
          {isDeleting ? 'Suppression...' : 'Supprimer'}
        </button>
      </div>
      <dl className="space-y-4">
        {fields.map(({ label, value }) => (
          <div key={label}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
            <dd className="mt-1 break-words text-sm text-gray-900">{value || '-'}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

function preinscriptionFields(item: PreinscriptionSubmission): Array<{ label: string; value: ReactNode }> {
  return [
    { label: 'Téléphone', value: item.phone },
    { label: 'Email', value: item.email },
    { label: 'Niveau', value: item.niveau },
    { label: 'Quartier', value: item.quartier },
    { label: 'Nombre enfants', value: item.nombreEnfants },
    { label: 'Origine', value: getOrigin(item) === 'meta' ? 'Meta Lead' : 'Site web' },
    { label: 'Source', value: item.source },
    {
      label: 'Statut',
      value: (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${preinscriptionStatusBadgeClass(item.status)}`}
        >
          {item.status}
        </span>
      ),
    },
    { label: 'Date RDV', value: item.rdvDate ? formatRdvDate(item.rdvDate) : null },
    { label: 'Heure RDV', value: item.rdvHeure },
    { label: 'RDV mis à jour', value: item.rdvUpdatedAt ? formatDate(item.rdvUpdatedAt) : null },
    { label: 'Message', value: item.message },
  ];
}

function jpoFields(item: JpoRsvpSubmission): Array<{ label: string; value: ReactNode }> {
  return [
    { label: 'Prénom enfant', value: item.prenomEnfant },
    { label: 'Téléphone', value: item.phone },
    { label: 'Email', value: item.email },
    { label: 'Niveau', value: item.niveau },
    { label: 'Origine', value: getOrigin(item) === 'meta' ? 'Meta Lead' : 'Site web' },
    { label: 'Statut', value: item.status },
    { label: 'UTM source', value: item.utmSource },
    { label: 'UTM medium', value: item.utmMedium },
    { label: 'UTM campagne', value: item.utmCampaign },
    { label: 'UTM contenu', value: item.utmContent },
    { label: 'UTM terme', value: item.utmTerm },
  ];
}

function postbacFields(item: PostbacSubmission): Array<{ label: string; value: ReactNode }> {
  return [
    { label: 'Pôle', value: item.pole },
    { label: 'Qui remplit', value: item.quiRemplit },
    { label: 'Téléphone', value: item.phone },
    { label: 'Email', value: item.email },
    { label: 'Origine', value: getOrigin(item) === 'meta' ? 'Meta Lead' : 'Site web' },
    { label: 'Source', value: item.source },
    {
      label: 'Statut',
      value: (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${preinscriptionStatusBadgeClass(item.status)}`}
        >
          {item.status}
        </span>
      ),
    },
    { label: 'UTM source', value: item.utmSource },
    { label: 'UTM medium', value: item.utmMedium },
    { label: 'UTM campagne', value: item.utmCampaign },
    { label: 'UTM contenu', value: item.utmContent },
    { label: 'UTM terme', value: item.utmTerm },
    { label: 'Message', value: item.message },
  ];
}

export default AdminApp;
